import { createCacheWrapper } from '@utils/cache';
import { DAYS_TO_MS, HOURS_TO_MS } from '@utils/constants';
import { sitecoreAxios } from './core.axios.js';
import { AccessCodes } from './typings.access-codes.js';
import { buildUrl } from './util.build-url.js';
import { parseAspectRatio } from './util.parse-aspect-ratio.js';

const cache = createCacheWrapper({
  timeToLiveMs: 1 * HOURS_TO_MS,
  evictAfterMs: 1 * DAYS_TO_MS,
  capacity: 1e4
});
function getNestedPages(portalConfig, pageId, options) {
  const levels = (options == null ? void 0 : options.levels) ?? 1;
  return cache(
    !(options == null ? void 0 : options.disableCache) ? `${portalConfig.id}-${pageId}-${levels}-${!!(options == null ? void 0 : options.previewMode)}` : void 0,
    async () => {
      const pageDescriptors = await fetchNestedPages(portalConfig, pageId, {
        ...options,
        levels
      });
      if (pageId === void 0) {
        const homePageDescriptor = pageDescriptors.find(
          (it) => it.path === "/"
        );
        if (homePageDescriptor && homePageDescriptor.hasChildren && !homePageDescriptor.children) {
          homePageDescriptor.children = await fetchNestedPages(
            portalConfig,
            homePageDescriptor.id,
            {
              ...options,
              levels: 1
            }
          );
        }
      }
      return pageDescriptors;
    }
  ).getStaleWhileRevalidate();
}
async function fetchNestedPages(portalConfig, pageId, options) {
  const url = buildUrl(
    `/api/v1/portals/${portalConfig.id}/items/${pageId ?? portalConfig.id}/navigation?levels=${options.levels}`,
    options
  );
  const response = await sitecoreAxios.get(url);
  return response.data.map((it) => mapResponseToPageDescriptor(portalConfig.homePath, it, pageId)).filter((it) => it !== void 0);
}
function mapResponseToPageDescriptor(homePath, response, parentId) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
  const type = (() => {
    switch (response.type) {
      case "Unknown":
        return "Page";
      case "Page":
      case "Course":
      case "ChapterGroup":
      case "Chapter":
      case "Library":
      case "Shelf":
      case "ThemePortalFrontPage":
      case "TeachersGuidePage":
        return response.type;
      default:
        console.warn(
          `api(pm2).mapResponseToPageDescriptor(): Invalid item type "${response.type}", assuming Page...`
        );
        return "Page";
    }
  })();
  const frontPageTopSection = (() => {
    const label = response["topstoryLabel"];
    const headline = response["topstoryHeadline"];
    const description = response["topstoryDescription"];
    const imageSrc = response["topstoryImage"];
    const imageHeight = response["topstoryImageHeight"] ? Number(response["topstoryImageHeight"]) : void 0;
    const imageWidth = response["topstoryImageWidth"] ? Number(response["topstoryImageWidth"]) : void 0;
    if (!headline || !description || !imageSrc) {
      return void 0;
    }
    return {
      label,
      headline,
      description,
      image: {
        src: `${process.env["PM2_MEDIA_DOMAIN"]}/${imageSrc}`,
        width: imageWidth || void 0,
        height: imageHeight || void 0
      }
    };
  })();
  return {
    id: response.id,
    parentId,
    title: response.title,
    description: response.description,
    label: response.label,
    path: response.path === homePath ? "/" : response.path,
    type,
    accessCode: response.access ?? AccessCodes.ALLOWED,
    grades: response.grades,
    children: response.children && response.children.length > 0 ? response.children.map(
      (child) => mapResponseToPageDescriptor(homePath, child, response.id)
    ).filter((child) => child !== void 0) : void 0,
    icon: response.icon,
    topNavIcon: response.iconTopMenu,
    image: response.image ? `${process.env["PM2_MEDIA_DOMAIN"]}${response.image}` : void 0,
    vignette: response.vignetteUrl ? {
      url: `${process.env["PM2_MEDIA_DOMAIN"]}${response.vignetteUrl}`,
      ...parseAspectRatio(response.vignetteAspectRatio)
    } : void 0,
    hasChildren: response.hasChildren,
    hasResources: response.hasResources,
    frontPageTopSection,
    megaMenu: {
      hasMegaMenu: ((_a = response.megaMenuTop) == null ? void 0 : _a.showMegaMenu) ?? false,
      terminateMegaMenu: ((_b = response.megaMenu) == null ? void 0 : _b.goToPage) ?? false,
      showGradeFilters: ((_c = response.megaMenuTop) == null ? void 0 : _c.showGradeFilter) ?? ((_d = response.megaMenu) == null ? void 0 : _d.showGradeFilter) ?? false,
      allFiltersSelectorText: ((_e = response.megaMenuTop) == null ? void 0 : _e.allFiltersSelectorText) ?? ((_f = response.megaMenu) == null ? void 0 : _f.allFiltersSelectorText) ?? "Alle",
      filterCategories: ((_g = response.megaMenu) == null ? void 0 : _g.filterCategories) ?? void 0,
      availableFilterCategories: ((_h = response.megaMenuTop) == null ? void 0 : _h.availableFilterCategories) ?? ((_i = response.megaMenu) == null ? void 0 : _i.availableFilterCategories),
      displayType: {
        levelOne: ((_j = response.megaMenuTop) == null ? void 0 : _j.levelOneDisplay) ?? void 0,
        levelTwo: ((_k = response.megaMenuTop) == null ? void 0 : _k.levelTwoDisplay) ?? void 0
      }
    }
  };
}

export { getNestedPages, mapResponseToPageDescriptor };
//# sourceMappingURL=request.get-nested-pages.js.map
